import { useState, useEffect } from 'react';
import { themeI18n } from '../utils/i18n_helper';
import { Accordion } from './Accordion.js';
import { PriceRangeSlider } from './PriceRangeSlider.js';
import { formatMoney } from '@shopify/theme-currency';
import '../../styles/components/filter-accordion.scss'

import Icons from './Icons.js';

const url = new URL(window.location.href);

const validSortOptions = [
  // "reviews", // Keeping in case they add a review app
  "relevancy",
  "a_to_z",
  "z_to_a",
  "price_min_to_max",
  "price_max_to_min",
  "creation_date_oldest",
  "creation_date",
  "popularity",
];

const listingData = JSON.parse(document.querySelector('[data-product-listing-data]').innerHTML);
const myStoreInitial = JSON.parse(localStorage.getItem('theme:my_store'));
const storeLocationsData = JSON.parse(document.querySelector('[data-store-locations-data]').innerHTML).locations;

export const Filters = ({ facets, sortBy, setSortBy, narrowBy, setNarrowBy, setPage, setPrevPage, setNextPage, name}) => {
  const [selectedFilters, setSelectedFilters] = useState(narrowBy);
  const [openedAccordions, setOpenedAccordions] = useState([]);
  const [myStoreLocation, setMyStoreLocation] = useState(myStoreInitial);
  const [filterLabels, setFilterLabels] = useState({});

  const priceMaxFacet = facets && facets.find(([facetName]) => facetName === "Price_max");
  const priceMax = priceMaxFacet && priceMaxFacet[1][0] || Infinity;
  const locationsFacet = facets && facets.find(([facetName]) => facetName === "Locations");
  const filteredFacets = facets && facets.filter(([facetName]) => facetName !== "Price" && facetName !== "Price_min" && facetName !== "Locations");

  const handleFilterChange = (facetName, value, trimmedLabel) => {
    let newFilters = [];
    if (facetName === 'Price_from_to') {
      const [minPriceValue, maxPriceValue] = value;
      const priceRangeValue = `${minPriceValue}-${maxPriceValue}`;
      newFilters = selectedFilters.filter(filter => filter[0] !== facetName);
      newFilters = [...newFilters, [facetName, priceRangeValue]];
      const filterFacetName = `filter.${facetName}`;
      setUrlParam(filterFacetName, priceRangeValue);

      // check if min price is 0 and max price is maximum
      if ((minPriceValue == 0) && (maxPriceValue == priceMax && priceMax)) {
        newFilters = selectedFilters.filter(filter => filter[0] !== facetName);
        deleteUrlParam(filterFacetName);
      }
    } else {
      const existingFilterIndex = selectedFilters.findIndex(filter => filter[0] === facetName && filter[1] === value);
      if (existingFilterIndex !== -1) {
        newFilters = [...selectedFilters];
        newFilters.splice(existingFilterIndex, 1);
        removeFilterUrlParam(facetName, value);
        setFilterLabels(prev => {
          const newFilterLabels = { ...prev };
          delete newFilterLabels[value];
          return newFilterLabels;
        })
      } else {
        newFilters = [...selectedFilters, [facetName, value]];
        setFilterUrlParam(facetName, value);
        setFilterLabels(prev => {
          const newFilterLabels = { ...prev };
          newFilterLabels[value] = trimmedLabel;
          return newFilterLabels;
        });
      }
    }

    resetPage();
    setSelectedFilters(newFilters);
    setNarrowBy(newFilters);
  };

  const setUrlParam = (paramName, paramValue) => {
    url.searchParams.set(paramName, paramValue);
    window.history.pushState(null, null, decodeURIComponent(url.toString()));
  }

  const deleteUrlParam = (paramName) => {
    url.searchParams.delete(paramName);
    window.history.pushState(null, null, decodeURIComponent(url.toString()));
  }

  const setFilterUrlParam = (paramName, paramValue, refresh = true) => {
    const filterParamName = `filter.${paramName}`;
    const existingParams = url.searchParams.getAll(filterParamName);
    if(existingParams.length > 0) {
      url.searchParams.delete(filterParamName);
      existingParams.forEach(existingValue => {
        url.searchParams.append(filterParamName, existingValue);
      });
    }
    url.searchParams.append(filterParamName, paramValue);
    if (refresh) {
      window.history.pushState(null, null, decodeURIComponent(url.toString()));
    }
  }

  const removeFilterUrlParam = (paramName, paramValue, refresh = true) => {
    const filterParamName = `filter.${paramName}`;
    const existingParams = url.searchParams.getAll(filterParamName);
    if(existingParams.length > 0) {
      const filteredParams = existingParams.filter(value => value !== paramValue);
      url.searchParams.delete(filterParamName);
      filteredParams.forEach(existingValue => {
        url.searchParams.append(filterParamName, existingValue);
      });
    }
    if(refresh) {
      window.history.pushState(null, null, decodeURIComponent(url.toString()));
    }
  }

  const RenderLocationsFacet = () => {
    if (!myStoreLocation) {
      return;
    }

    const myStoreData = storeLocationsData.find(location => location.id === myStoreLocation);

    const myStoreFacet = locationsFacet && locationsFacet[1].find(([locationName, count]) => locationName.includes(myStoreData.store_number));

    if (myStoreFacet) {
      return (
        <div className='product-listing__location-filter'>
          <label className="product-listing__facet-checkbox checkbox">
            <input
              type="checkbox"
              name="Locations"
              value={myStoreFacet[0]}
              onChange={(e) => handleFilterChange('Locations', e.target.value)}
              checked={selectedFilters.some(filter => filter[0] === 'Locations' && filter[1] === myStoreFacet[0])}
            />
            <div className="checkbox__box"></div>

            <span className="product-listing__facet-option checkbox__label">
              {themeI18n('shopify.filters.availability.available_at')} <span className='product-listing__location-highlight'>{myStoreData.name}</span>
            </span>
          </label>
        </div>
      )
    }
  }

  const removeUrlParamsWithPrefix = (prefix) => {
    const paramsToDelete = [];
    url.searchParams.forEach((_, key) => {
      if (key.startsWith(prefix)) {
        paramsToDelete.push(key);
      }
    });
    paramsToDelete.forEach(param => {
      url.searchParams.delete(param);
    });
    window.history.pushState(null, null, decodeURIComponent(url.toString()));
  };

  const clearAllFilters = () => {
    setSelectedFilters([]);
    setNarrowBy([]);
    removeUrlParamsWithPrefix('filter');
    resetPage();
  }

  const removeFilter = (facetName, value) => {
    const newFilters = selectedFilters.filter(filter => filter[0] !== facetName || filter[1] !== value);
    setSelectedFilters(newFilters);
    setNarrowBy(newFilters);
    removeFilterUrlParam(facetName, value);
    resetPage();
  }

  const getCategoryTitleById = (categoryId) => {
    const categoriesFacet = facets.find(([facetName]) => facetName === "Categories");
    if (categoriesFacet) {
      const category = categoriesFacet[1].find(([id]) => id === categoryId);
      if (category) {
        return category[2];
      }
    }
    return null;
  };

  const resetPage = () => {
    setPrevPage(0);
    setNextPage(2);
    setPage(1);

    url.searchParams.set('page', 1);
    window.history.pushState(null, null, url.toString());
  }

  useEffect(() => {
    window.addEventListener('myStoreChanged', () => {
      const newStoreData = JSON.parse(localStorage.getItem('theme:my_store'));

      setMyStoreLocation(newStoreData);
    });

    return () => {
      window.removeEventListener('myStoreChanged', () => {
        const newStoreData = JSON.parse(localStorage.getItem('theme:my_store'));

        setMyStoreLocation(newStoreData);
      });
    }
  });

  // Update drawer header with filter count
  useEffect(() => {
    const drawerHeader = document.querySelector('.drawer__details[open] .drawer__header-content');
    
    if (drawerHeader) {
      const baseTitle = listingData.enableFilters 
        ? themeI18n('collection.filters.title') 
        : themeI18n('collection.filters.sort_title');
      
      const countText = selectedFilters.length > 0 ? ` (${selectedFilters.length})` : '';
      drawerHeader.innerHTML = `<p>${baseTitle}${countText}</p>`;
    }
  }, [selectedFilters]);

  return (
    <div className='product-listing__filters'>

      {listingData.enableFilters && (
        <>
          {selectedFilters.length > 0 && (
            <div className='product-listing__selected-filters-list'>
              {selectedFilters.map(([facetName, facetValue]) => {
                let value = getCategoryTitleById(facetValue) || facetValue;

                if (facetName === 'Price_from_to') {
                  const minPriceValue = facetValue.split("-")[0];
                  const minPriceValueCurrency = formatMoney(parseFloat(minPriceValue * 100), "{{amount_with_comma_separator}}$");
                  const maxPriceValue = facetValue.split("-")[1];
                  const maxPriceValueCurrency = formatMoney(parseFloat(maxPriceValue * 100), "{{amount_with_comma_separator}}$");
                  value = `${minPriceValueCurrency}-${maxPriceValueCurrency}`;
                }

                if (facetName === 'Isp-review-stars') {
                  value = `${value} ${themeI18n('collection.filters.stars')}`;
                }

                return (
                  <button onClick={()=> removeFilter(facetName, facetValue)} key={facetName + facetValue} className='product-listing__selected-filter' title={themeI18n('collection.filters.remove_filter') + ": " + (filterLabels[value] || value)}>
                    <span className='product-listing__selected-filter-name'>
                      {filterLabels[value] || value}
                    </span>
                    <span className='product-listing__selected-filter-remove'>
                      <Icons icon='close' size='small' />
                    </span>
                  </button>
                );
              })}

              {selectedFilters.length > 0 && (
                <button onClick={clearAllFilters} className='product-listing__selected-filters-clear'>{themeI18n('collection.filters.clear_all')}</button>
              )}
            </div>
          )}
        </>
      )}

      {listingData.enableSort && (
        <div className='product-listing__sort-by'>
          <label className='product-listing__sort-by-label' htmlFor='sort-by'>
            {themeI18n('collection.sort_by_label')}
          </label>

          <select
            id='sort-by'
            value={sortBy}
            onChange={(event)=> {
              url.searchParams.set('sort_by', event.target.value);
              url.searchParams.set('page', 1);
              window.history.pushState(null, null, url.toString());

              setPage(1);
              setSortBy(event.target.value);
              setPrevPage(0);
              setNextPage(2);
            }}
          >
            {validSortOptions.map((option) => {
              return (
                <option key={option} value={option}>
                  {themeI18n(`shopify.collections.sorting_fs.${option}`)}
                </option>
              )
            })}
          </select>
        </div>
      )}

      {listingData.enableFilters && (
        <>
          {/* {selectedFilters.length > 0 && (
              <div className='product-listing__selected-filters-list'>
                {selectedFilters.map(([facetName, facetValue]) => {
                  let value = getCategoryTitleById(facetValue) || facetValue;

                  if (facetName === 'Price_from_to') {
                    const minPriceValue = facetValue.split("-")[0];
                    const minPriceValueCurrency = formatMoney(parseFloat(minPriceValue * 100), "{{amount_with_comma_separator}}$");
                    const maxPriceValue = facetValue.split("-")[1];
                    const maxPriceValueCurrency = formatMoney(parseFloat(maxPriceValue * 100), "{{amount_with_comma_separator}}$");
                    value = `${minPriceValueCurrency}-${maxPriceValueCurrency}`;
                  }

                  if (facetName === 'Isp-review-stars') {
                    value = `${value} ${themeI18n('collection.filters.stars')}`;
                  }

                  return (
                    <button onClick={()=> removeFilter(facetName, facetValue)} key={facetName + facetValue} className='product-listing__selected-filter' title={themeI18n('collection.filters.remove_filter') + ": " + (filterLabels[value] || value)}>
                      <span className='product-listing__selected-filter-name'>
                        {filterLabels[value] || value}
                      </span>
                      <span className='product-listing__selected-filter-remove'>
                        <Icons icon='close' size='small' />
                      </span>
                    </button>
                  );
                })}
              </div>
            )} */}

          <RenderLocationsFacet />
          <div className='product-listing__accordions'>
            <RenderPriceMaxFacet priceMaxFacet={priceMaxFacet} setOpenedAccordions={setOpenedAccordions} openedAccordions={openedAccordions} name={name} priceMax={priceMax} selectedFilters={selectedFilters} handleFilterChange={handleFilterChange} />
            <RenderFilteredFacets filteredFacets={filteredFacets} setOpenedAccordions={setOpenedAccordions} openedAccordions={openedAccordions} selectedFilters={selectedFilters} handleFilterChange={handleFilterChange} name={name} />
          </div>
        </>
      )}
    </div>
  )
};

const RenderPriceMaxFacet = ({ priceMaxFacet, setOpenedAccordions, openedAccordions, name, priceMax, selectedFilters, handleFilterChange}) => {
  if (!priceMaxFacet) return null;

  return (
    <Accordion title={themeI18n('shopify.filters.price')} id={themeI18n('shopify.filters.price') + name} setOpenedAccordions={setOpenedAccordions} openedAccordions={openedAccordions}>
      <PriceRangeSlider
        priceMax={priceMax}
        selectedFilters={selectedFilters}
        handleFilterChange={handleFilterChange}
      />
    </Accordion>
  );
};

const RenderFilteredFacets = ({ filteredFacets, setOpenedAccordions, openedAccordions, selectedFilters, handleFilterChange, name }) => {
  return filteredFacets.map(([facetName, options, label, isRange, sortOrder]) => {
    if (!isRange) {
      return (
        <Accordion key={facetName} title={label} id={label + name} setOpenedAccordions={setOpenedAccordions} openedAccordions={openedAccordions}>
          {options.map(([optionValue, count, trimmedLabel, categoryTitle], index) => {
            const isSelectedFilter = selectedFilters.some(filter => filter[0] === facetName && filter[1] === optionValue);
            const isCategories = facetName.toLowerCase() === 'categories';
            const isRating = facetName.toLowerCase() === 'isp-review-stars';

            return (
              <label key={optionValue} className="product-listing__facet-checkbox checkbox" style={{order: isRating ? options.length - index : 0}}>
                <input
                  type="checkbox"
                  name={facetName}
                  value={optionValue}
                  onChange={(e) => handleFilterChange(facetName, e.target.value, trimmedLabel)}
                  checked={isSelectedFilter}
                />
                <div className="checkbox__box"></div>
                <span className="product-listing__facet-option checkbox__label">
                  <RenderFilterLabel
                    optionValue={optionValue}
                    trimmedLabel={trimmedLabel}
                    categoryTitle={categoryTitle}
                    count={count}
                    isRating={isRating}
                    isCategories={isCategories}
                  />
                </span>
              </label>
            );
          })}
        </Accordion>
      );
    }
  });
};

const RenderFilterLabel = ({ optionValue, trimmedLabel, categoryTitle, count, isRating, isCategories }) => {
  if (isRating) {
    return (
      <>
        <RenderStarsRating rating={optionValue} />
      </>
    );
  } else if (isCategories) {
    return (
      <>
        {categoryTitle} {`(${count})`}
      </>
    );
  } else {
    return (
      <>
        {optionValue} {`(${count})`}
      </>
    );
  }
}

const RenderStarsRating = ({ rating }) => {
  const stars = [];
  for (let i = 0; i < 5; i++) {
    if (i < rating) {
      stars.push(<Icons icon='star-fill' key={i} />);
    } else {
      stars.push(<Icons icon='star' key={i} />);
    }
  }
  return (
    <span className="product-listing__stars-rating">{stars}</span>
  );
}
